import React, { useState, ChangeEvent, useContext, useEffect } from 'react';
import { EyeIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Input } from 'common/input';
import { Spinner } from 'common/spinner';
import { errorsCode } from 'config/constants';
import { AppContext } from 'context';
import CypressIds from 'cypressIds';
import { trackCustomEvent } from 'helpers/analytics';
import {
  classnames,
  getInitials,
  hasNotUserValidatedEmail,
  isEmailValid,
  redictDependingFailingStatus,
} from 'helpers/utils';
import { UserController } from 'networking/controllers/user-controller';
import { Notification } from 'common/notification';
import { goToPage, RouteName } from 'routes';
import { LocalStorageApi } from 'helpers/local-storage';
import { UserSignIn } from 'models/user-sign-in';
import { UserInitials } from 'common/user-initials/user-initials';
import styles from './sign-in-form.module.scss';

type SignInProps = {
  email: string;
  ownerName: string;
  vaultOwnerName: string;
  vaultId: string;
  handleResetPassword: () => void;
};

type Mandatory = {
  email: string;
  password: string;
};

const mandatoryFieldsErrors: Mandatory = {
  email: '',
  password: '',
};

const SignInForm: React.FC<SignInProps> = ({
  email,
  vaultOwnerName,
  ownerName,
  vaultId,
  handleResetPassword,
}) => {
  const [userState, setUserState] = useState<UserSignInType>(new UserSignIn());
  const [errors, setErrors] = useState<MandatoryFieldsType>(
    mandatoryFieldsErrors,
  );
  const [inputPassword, setInputPassword] = useState<boolean>(true);
  const [showError, setShowError] = useState(false);

  const [fetching, setFetching] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  const { state } = useContext(AppContext);

  const auth = async () => {
    if (state.logged) {
      const wasRedirected = redictDependingFailingStatus(state.data.user);

      if (!wasRedirected) {
        goToPage(RouteName.OnboardingSubjectSelection, {}, { vaultId });
      }

      return;
    }

    setFetching(true);

    try {
      const data = await UserController.me();

      const wasRedirected = redictDependingFailingStatus(data.user);

      if (!wasRedirected) {
        goToPage(RouteName.OnboardingSubjectSelection, {}, { vaultId });
      }
    } catch (e) {
      setShowSignIn(true);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    auth();

    const contributorEmail = email;

    if (contributorEmail) {
      setUserState((prevUserState) => ({
        ...prevUserState,
        email: contributorEmail,
      }));
    }

    return () => {
      if (!hasNotUserValidatedEmail(state.data.user)) {
        LocalStorageApi.set('previous-url', '');
      }
    };
  }, []);

  const addBackEndErrorsToFields = (backEndErrors: MandatoryFieldsType) => {
    const errorsCopy = { ...errors };

    Object.keys(errorsCopy).forEach((field) => {
      errorsCopy[field] = backEndErrors[field]
        ? backEndErrors[field].toUpperCase()
        : '';
    });

    setErrors(errorsCopy);
  };

  const validateField = <T extends keyof Mandatory>(field: T) => {
    const emptyFieldMessage =
      userState[field].trim() === ''
        ? `${field.toUpperCase()} shouldn't be empty`
        : '';
    setErrors((prevState) => ({
      ...prevState,
      [field]: emptyFieldMessage.toUpperCase(),
    }));

    if (field === 'email') {
      const emailMessage = !isEmailValid(userState[field])
        ? 'invalid email'
        : '';
      setErrors((prevState) => ({
        ...prevState,
        [field]: emailMessage.toUpperCase(),
      }));
    }
  };

  const handleLogin = async () => {
    Object.keys(mandatoryFieldsErrors).forEach((field) => {
      validateField(field as keyof typeof mandatoryFieldsErrors);
    });

    setFetching(true);

    try {
      const data = await UserController.signInUser(userState);

      trackCustomEvent('logIn');

      const wasRedirected = redictDependingFailingStatus(data.user);

      if (!wasRedirected)
        goToPage(RouteName.OnboardingSubjectSelection, {}, { vaultId });
    } catch (err: any) {
      const errorCredentials = err?.errors?.find(
        (e: any) => e.errorCode === errorsCode.credentialsError,
      );

      if (errorCredentials) {
        const objectErrors = {
          email: ' ',
          password: 'Incorrect email or password.',
        };

        addBackEndErrorsToFields!(objectErrors);
      } else {
        setShowError(true);
      }
      setFetching(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUserState((prevUserState) => ({ ...prevUserState, [name]: value }));
  };

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  const forgotPassword = () => (
    <div>
      <Button
        buttonSize={ButtonSize.Small}
        buttonStyle={ButtonStyle.PrimaryGhost}
        onClick={handleResetPassword}
        className={styles.buttonPassword}
      >
        Forgot your password?
      </Button>
    </div>
  );

  const showFormSignIn = () => (
    <>
      <UserInitials name={getInitials(vaultOwnerName)} />
      <div className={classnames(styles.containerTitle)}>
        <div className={classnames(styles.title, 'text__heading5__gray800')}>
          <span>{vaultOwnerName}</span>
          <span className={classnames('text__body__regular__large__gray800')}>
            invited you to collaborate
          </span>
        </div>
        <div className={styles.lineSeparator} />
        <div className={styles.subtitle}>
          <span
            className={classnames(
              styles.subtitleText,
              'text__body__regular__large__gray800',
            )}
          >
            Log in to continue
          </span>
          <div className={styles.containerUserData}>
            <div className={styles.containerInitials}>
              <div className="text__body__regular__tiny__success70">
                {getInitials(ownerName)}
              </div>
            </div>
            <span className="text__body__bold__small__gray700">{email}</span>
          </div>
        </div>
      </div>

      <Input
        id="password"
        name="password"
        label="Enter password"
        placeholder="Enter password"
        value={userState.password}
        type={inputPassword ? 'password' : 'text'}
        errorMessage={errors.password}
        withIconEnd={
          <EyeIcon onClick={() => setInputPassword(!inputPassword)} />
        }
        onChange={handleChange}
        onBlur={() => validateField('password')}
        innerClassName={styles.input}
        dataCy={CypressIds.passwordSignin}
      />
      {forgotPassword()}
      <Button
        buttonStyle={ButtonStyle.PrimaryFilled}
        buttonSize={ButtonSize.Medium}
        className={styles.button}
        onClick={handleLogin}
      >
        {fetching ? (
          <Spinner color="white" className="spinner-inside-button" />
        ) : (
          <> Log in </>
        )}
      </Button>
    </>
  );

  return (
    <div className={styles.container}>
      {showSignIn ? (
        <>
          {showError && showErrorNotification()}
          {showFormSignIn()}
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
};

export { SignInForm };
